import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useI18n } from '../i18n/I18nProvider.js';
import { Button } from '../ui/index.js';

/**
 * Triage: the decision queue one card at a time.
 *
 * A person clearing forty proposals does not want forty scroll positions; they want one question
 * at a time and a fast, safe way to answer it. The rules here are the ones `docs/MOBILE.md` wrote
 * for exactly this surface, applied to the web:
 *
 * **A gesture may approve a low-risk, reversible decision only.** The risk label exists so that a
 * decision where being wrong is expensive gets looked at on its own; letting a thumb approve one
 * makes the label mean nothing. Higher risk keeps its explicit button. "Not now" is allowed at any
 * risk, because deferring is not acting.
 *
 * **Swiping is for touchscreens.** On a fine pointer the same deck is driven by keys and buttons —
 * a mouse drag pretending to be a thumb is neither fast nor safe, and desktop mail triage is a
 * keyboard's home ground. The keys follow `docs/UX.md`: bare keys only for what the undo toast can
 * take back, nothing while an input has focus.
 *
 * **A gesture is held locally for eight seconds rather than sent and reversed.** Sending
 * immediately makes undo a second write that can fail on its own. Held means a closed tab loses
 * nothing but an intention: the decision simply stays pending, which is the safe state.
 *
 * **Saying no still asks why.** The reasons panel is the only path to a rejection here, exactly as
 * in the list. A no that teaches nothing means the same proposal returns tomorrow.
 */

const HOLD_MS = 8_000;
/** Past this fraction of the card's width, release commits the gesture. */
const COMMIT_FRACTION = 0.4;

export interface TriageDecision {
  readonly id: string;
  readonly risk: 'low' | 'medium' | 'high';
  readonly reversible: boolean;
  readonly titleKey: string;
  readonly titleParams: Record<string, unknown>;
  readonly affectedCount: number;
  readonly autoApproved: boolean;
  readonly preview: {
    readonly examples: readonly { id: string; subject: string; from: string }[];
    readonly undoKey: string;
    readonly complete: boolean;
    readonly total: number;
  } | null;
}

export interface TriageDeckProps<T extends TriageDecision> {
  /**
   * A snapshot, not a live query. The deck advances a cursor over what was on the table when
   * triage began; a list that reshuffles under a person mid-flow answers questions they were
   * not looking at.
   */
  readonly decisions: readonly T[];
  readonly corrections: readonly string[];
  /** Sends the answer to the server. The deck has already waited out the hold. */
  readonly onApprove: (decision: T) => Promise<void>;
  readonly onReject: (decision: T, correction: string) => Promise<void>;
  readonly onExhausted: () => void;
}

interface HeldAction<T extends TriageDecision> {
  readonly decision: T;
  readonly kind: 'approve' | 'not_now';
  readonly timer: ReturnType<typeof setTimeout>;
}

function gestureMayApprove(decision: TriageDecision): boolean {
  return decision.risk === 'low' && decision.reversible;
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    target.isContentEditable
  );
}

export function TriageDeck<T extends TriageDecision>({
  decisions,
  corrections,
  onApprove,
  onReject,
  onExhausted,
}: TriageDeckProps<T>): ReactNode {
  const { t, translator } = useI18n();

  const [cursor, setCursor] = useState(0);
  const [asking, setAsking] = useState(false);
  const [held, setHeld] = useState<HeldAction<T> | null>(null);
  const [drag, setDrag] = useState<{ x: number; width: number } | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const pointerStart = useRef<number | null>(null);

  /*
   * Touch capability, asked of the medium rather than sniffed from the agent. A convertible with
   * both inputs gets both: coarse enables the drag, and the keys work regardless.
   */
  const coarse = useMemo(() => window.matchMedia('(pointer: coarse)').matches, []);
  const reducedMotion = useMemo(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  );

  const current = decisions[cursor];

  const commit = useCallback(
    (decision: T, kind: 'approve' | 'not_now') => {
      /*
       * The timer keeps running even if this component unmounts: the toast promised the action,
       * and a navigation is not a change of mind. Only the undo button is.
       */
      const timer = setTimeout(() => {
        setHeld((pending) => (pending?.decision.id === decision.id ? null : pending));
        void (kind === 'approve' ? onApprove(decision) : onReject(decision, 'not_now'));
      }, HOLD_MS);
      setHeld({ decision, kind, timer });
      setAsking(false);
      setDrag(null);
      setCursor((index) => index + 1);
    },
    [onApprove, onReject],
  );

  const undo = useCallback(() => {
    if (held === null) return;
    clearTimeout(held.timer);
    setHeld(null);
    /* The card returns to the front of the deck: an undo is a request to look again, now. */
    setCursor((index) => Math.max(0, index - 1));
  }, [held]);

  /* Rejection with a reason is immediate: a person who picked "wrong subject" has already looked. */
  const rejectWith = useCallback(
    (decision: T, correction: string) => {
      setAsking(false);
      setDrag(null);
      setCursor((index) => index + 1);
      void onReject(decision, correction);
    },
    [onReject],
  );

  useEffect(() => {
    if (cursor >= decisions.length && held === null) onExhausted();
  }, [cursor, decisions.length, held, onExhausted]);

  /* Keyboard triage, per docs/UX.md: nothing while typing, bare keys only for what the toast can
     take back. Approve stays off the keyboard for anything a gesture may not approve either. */
  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      if (current === undefined || isTypingTarget(event.target)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === 'a' && gestureMayApprove(current)) {
        event.preventDefault();
        commit(current, 'approve');
      } else if (event.key === 'n') {
        event.preventDefault();
        commit(current, 'not_now');
      } else if (event.key === 'r') {
        event.preventDefault();
        setAsking(true);
      } else if (event.key === 'z' && held !== null) {
        event.preventDefault();
        undo();
      } else if (event.key === 'Escape' && asking) {
        setAsking(false);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [current, held, asking, commit, undo]);

  /* Pointer-drag swiping, only where the pointer is a thumb. */
  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!coarse || current === undefined || asking) return;
      pointerStart.current = event.clientX;
      cardRef.current?.setPointerCapture(event.pointerId);
    },
    [coarse, current, asking],
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (pointerStart.current === null || current === undefined) return;
      const width = cardRef.current?.offsetWidth ?? 1;
      let x = event.clientX - pointerStart.current;
      /*
       * A card that may not be approved by gesture resists rightward drag instead of ignoring
       * it: resistance teaches "this one is deliberate" at the moment of the attempt, where a
       * dead gesture just feels broken.
       */
      if (x > 0 && !gestureMayApprove(current)) x = Math.min(x / 4, width * 0.1);
      setDrag({ x, width });
    },
    [current],
  );

  const onPointerUp = useCallback(() => {
    if (pointerStart.current === null || current === undefined) return;
    pointerStart.current = null;
    if (drag === null) return;
    const fraction = drag.x / drag.width;
    if (fraction <= -COMMIT_FRACTION) {
      commit(current, 'not_now');
    } else if (fraction >= COMMIT_FRACTION && gestureMayApprove(current)) {
      commit(current, 'approve');
    } else {
      setDrag(null);
    }
  }, [current, drag, commit]);

  if (current === undefined) {
    return (
      <section className="card triage__done">
        <p>
          <strong>{t('triage.done.title')}</strong>
        </p>
        {held !== null && <UndoToast held={held} onUndo={undo} />}
      </section>
    );
  }

  const dragStyle =
    drag === null || reducedMotion
      ? undefined
      : {
          transform: `translateX(${drag.x}px) rotate(${(drag.x / drag.width) * 6}deg)`,
        };

  const swipable = gestureMayApprove(current);

  return (
    <div className="triage">
      <p className="triage__progress">
        {t('triage.progress', { done: cursor, total: decisions.length })}
      </p>

      <div
        className={`card triage__card${drag !== null ? ' triage__card--dragging' : ''}`}
        ref={cardRef}
        style={dragStyle}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => {
          pointerStart.current = null;
          setDrag(null);
        }}
      >
        <div className="decision__head">
          <span className={`badge badge--${current.risk === 'high' ? 'critical' : 'high'}`}>
            {t(`decision.risk.${current.risk}`)}
          </span>
          <h2 className="decision__title">
            {translator.t(current.titleKey, current.titleParams as Record<string, string>)}
          </h2>
        </div>

        <p className="decision__meta">
          {current.reversible ? t('decision.reversible') : t('decision.irreversible')}
          {current.autoApproved && ` · ${t('decision.autoApproved')}`}
        </p>

        {current.preview !== null && (
          <div className="decision__preview">
            <ul className="today-list">
              {current.preview.examples.slice(0, 3).map((example) => (
                <li className="today-list__item" key={example.id}>
                  <span className="today-list__subject">{example.subject}</span>
                  <span className="today-list__meta">{example.from}</span>
                </li>
              ))}
            </ul>
            <p className="decision__meta">{translator.t(current.preview.undoKey)}</p>
          </div>
        )}

        {asking ? (
          <div className="triage__reasons" role="group" aria-label={t('decision.why.title')}>
            {corrections.map((correction) => (
              <Button key={correction} onClick={() => rejectWith(current, correction)}>
                {t(`decision.why.${correction}`)}
              </Button>
            ))}
            <Button onClick={() => setAsking(false)}>{t('common.cancel')}</Button>
          </div>
        ) : (
          <div className="triage__actions">
            <Button onClick={() => commit(current, 'not_now')}>{t('decision.why.not_now')}</Button>
            <Button onClick={() => setAsking(true)}>{t('decision.reject')}</Button>
            {/* The explicit button approves at any risk; only gestures and keys are gated. */}
            <Button onClick={() => commit(current, 'approve')}>{t('decision.approve')}</Button>
          </div>
        )}

        {/* What the medium affords, said once, quietly. */}
        <p className="triage__hint">
          {coarse
            ? swipable
              ? t('triage.hint.swipe')
              : t('triage.hint.deliberate')
            : swipable
              ? t('triage.hint.keys')
              : t('triage.hint.keysDeliberate')}
        </p>
      </div>

      {held !== null && <UndoToast held={held} onUndo={undo} />}
    </div>
  );
}

function UndoToast<T extends TriageDecision>({
  held,
  onUndo,
}: {
  held: HeldAction<T>;
  onUndo: () => void;
}): ReactNode {
  const { t } = useI18n();
  return (
    <div className="triage__toast" role="status" aria-live="polite">
      <span>
        {held.kind === 'approve' ? t('triage.toast.approved') : t('triage.toast.deferred')}
      </span>
      <Button onClick={onUndo}>{t('triage.toast.undo')}</Button>
    </div>
  );
}
